// The creator view's pure core (design_documents/Comics/media-tracker-comics-spec.md,
// "Creator view"): what a creator did on an issue, the role filter, grouping
// issues into runs, and the pacing rules for the background role fetch.
//
// Metron's issue LIST rows carry no credits, so a creator's role on an issue is
// only known once that issue's detail is cached. Until then the issue is
// "pending": shown (runs render immediately, unfiltered) and refined as details
// arrive.

import { ROLE_GROUPS, seriesLabel, collapseIssueNumbers, compareIssueNumbers, rangesInclude } from "./comics.js";

export const ROLE_FILTERS = [
	{ value: "art", label: "Art" },
	{ value: "writer", label: "Writing" },
	{ value: "all", label: "Everything" },
];

// What `creatorId` did on an issue, from its cached detail (worker /comic/:id).
// → { known: false } when the detail isn't cached yet; otherwise
//   { known: true, roles: [...], groups: Set("writer" | "art" | "cover") }.
export function creditFor(detail, creatorId) {
	if (!detail) return { known: false };
	const credit = (detail.credits ?? []).find((c) => Number(c.creator_id) === Number(creatorId));
	const roles = credit?.roles ?? [];
	const groups = new Set();
	for (const [group, names] of Object.entries(ROLE_GROUPS)) {
		if (roles.some((r) => names.includes(r))) groups.add(group);
	}
	return { known: true, roles, groups };
}

// Cover-only = credited for the cover and nothing on the interior story.
export function isCoverOnly(credit) {
	return !!credit?.known && credit.groups.has("cover") && !credit.groups.has("writer") && !credit.groups.has("art");
}

// Does an issue pass the role filter? Pending issues always do (provisionally).
export function passesFilter(credit, filter, showCoverOnly = false) {
	if (!credit?.known) return true;
	if (isCoverOnly(credit)) return showCoverOnly;
	if (filter === "writer") return credit.groups.has("writer");
	if (filter === "art") return credit.groups.has("art");
	return true;
}

// Group a creator's issues into runs (one per Metron series — Metron already
// splits relaunches into their own series). Runs are ordered by first cover
// date; issue numbers collapse into ranges ("#188–193, #197–200").
//   issues   — list rows ({ issue_id, series: {id,name,year_began}, number, cover_date })
//   details  — Map(issue_id → cached detail) for the credits
//   filter   — "art" | "writer" | "all"
// Returns { runs, total, shown, pending, coverOnlyHidden }.
export function buildRuns(issues, { creatorId, details = new Map(), filter = "all", showCoverOnly = false } = {}) {
	const bySeries = new Map();
	let shown = 0;
	let pending = 0;
	let coverOnlyHidden = 0;
	for (const issue of issues ?? []) {
		const credit = creditFor(details.get(issue.issue_id), creatorId);
		if (!credit.known) pending++;
		if (!passesFilter(credit, filter, showCoverOnly)) {
			if (isCoverOnly(credit)) coverOnlyHidden++;
			continue;
		}
		shown++;
		const sid = issue.series?.id ?? `?${issue.series?.name ?? ""}`;
		if (!bySeries.has(sid)) {
			bySeries.set(sid, { series: issue.series ?? null, issues: [] });
		}
		bySeries.get(sid).issues.push({ ...issue, credit });
	}

	const collaboratorGroup = filter === "writer" ? "art" : "writer";
	const runs = [];
	for (const [sid, { series, issues: list }] of bySeries) {
		list.sort((a, b) => compareIssueNumbers(a.number ?? "", b.number ?? ""));
		const dates = list.map((i) => i.cover_date).filter(Boolean).sort();
		runs.push({
			series_id: typeof sid === "number" ? sid : null,
			label: seriesLabel(series) ?? "Unknown series",
			issues: list,
			ranges: collapseIssueNumbers(list.map((i) => i.number).filter((n) => n != null)),
			first_date: dates[0] ?? null,
			last_date: dates[dates.length - 1] ?? null,
			pending: list.filter((i) => !i.credit.known).length,
			collaborators: collaborators(list, details, creatorId, collaboratorGroup),
		});
	}
	runs.sort((a, b) => {
		if (a.first_date && b.first_date) return a.first_date.localeCompare(b.first_date) || a.label.localeCompare(b.label);
		if (a.first_date) return -1;
		if (b.first_date) return 1;
		return a.label.localeCompare(b.label);
	});
	return { runs, total: (issues ?? []).length, shown, pending, coverOnlyHidden };
}

// The people most often credited in `group` on a run's issues (excluding the
// creator), most frequent first, at most three.
function collaborators(list, details, creatorId, group) {
	const counts = new Map();
	for (const issue of list) {
		const detail = details.get(issue.issue_id);
		for (const c of detail?.credits ?? []) {
			if (Number(c.creator_id) === Number(creatorId)) continue;
			if (!(c.roles ?? []).some((r) => ROLE_GROUPS[group].includes(r))) continue;
			counts.set(c.name, (counts.get(c.name) ?? 0) + 1);
		}
	}
	return [...counts.entries()]
		.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
		.slice(0, 3)
		.map(([name]) => name);
}

// ---------- pacing ----------

// One Metron call every 4 s = 15/min, under the 20/min burst limit with room for
// the user's own searches. An edge-cache HIT cost Metron nothing, so the next
// call can follow almost at once.
export const ROLE_FETCH_INTERVAL_MS = 4000;
// Stop for the day below this many calls left, so searches and adds keep working.
export const DAILY_RESERVE = 300;

export function nextDelayMs(meta, { interval = ROLE_FETCH_INTERVAL_MS, now = Date.now() } = {}) {
	if (meta?.cache === "HIT") return 150;
	if (meta?.burstRemaining != null && meta.burstRemaining <= 2 && meta?.burstReset) {
		return Math.max(interval, meta.burstReset * 1000 - now + 1000);
	}
	return interval;
}

export function dailyBudgetLow(meta, reserve = DAILY_RESERVE) {
	return meta?.dailyRemaining != null && meta.dailyRemaining < reserve;
}

// ---------- collections overlay (Phase 4) ----------

// Owning = "have it". Had it / borrowed / a friend's copy don't count as owned.
function owns(comic) {
	return comic?.ownership_status === "have_it";
}

// Every collection in the library that says what it contains — the user's
// "Collects…" entries plus Metron's own reprint list — in a form runs can be
// matched against. Single issues are ignored here (see ownedIssueIds).
export function collectionIndex(comics) {
	const out = [];
	for (const c of comics ?? []) {
		const entries = (c.collects ?? []).map((e) => ({
			series_id: e.series_id ?? null,
			label: String(e.series_label ?? "").trim().toLowerCase(),
			ranges: e.ranges,
		}));
		const reprintIds = new Set((c.reprints ?? []).map((r) => Number(r.id)));
		if (entries.length === 0 && reprintIds.size === 0) continue;
		out.push({ comic: c, owned: owns(c), entries, reprintIds });
	}
	return out;
}

// Metron issue ids the user owns as single issues.
export function ownedIssueIds(comics) {
	return new Set(
		(comics ?? [])
			.filter((c) => c.source === "metron" && c.format === "issue" && owns(c))
			.map((c) => Number(c.external_id)),
	);
}

function entryCovers(entry, run, issue) {
	const sameSeries =
		entry.series_id != null
			? run.series_id != null && Number(entry.series_id) === Number(run.series_id)
			: entry.label === String(run.label ?? "").toLowerCase();
	return sameSeries && rangesInclude(entry.ranges, issue.number);
}

// Overlay one run (from buildRuns): which collections cover which of its issues,
// and each issue's status —
//   "owned"       in a collection you own, or the single issue itself is owned
//   "collected"   in a collection you know of (in your library) but don't own
//   "uncollected" no known collection
// → { collections: [{ id, title, format, owned, numbers, ranges }],
//     status: Map(issue_id → status), counts: { owned, collected, uncollected } }
export function overlayRun(run, index, ownedSingles = new Set()) {
	const status = new Map();
	const byCollection = new Map();
	for (const issue of run.issues) {
		let best = "uncollected";
		for (const col of index) {
			const covers =
				col.reprintIds.has(Number(issue.issue_id)) || col.entries.some((e) => entryCovers(e, run, issue));
			if (!covers) continue;
			if (!byCollection.has(col.comic.id)) byCollection.set(col.comic.id, { col, numbers: [] });
			byCollection.get(col.comic.id).numbers.push(issue.number);
			if (col.owned) best = "owned";
			else if (best === "uncollected") best = "collected";
		}
		if (best !== "owned" && ownedSingles.has(Number(issue.issue_id))) best = "owned";
		status.set(issue.issue_id, best);
	}
	const counts = { owned: 0, collected: 0, uncollected: 0 };
	for (const s of status.values()) counts[s]++;
	const collections = [...byCollection.values()]
		.map(({ col, numbers }) => ({
			id: col.comic.id,
			title: col.comic.title,
			format: col.comic.format ?? null,
			owned: col.owned,
			numbers,
			ranges: collapseIssueNumbers(numbers),
		}))
		.sort((a, b) => Number(b.owned) - Number(a.owned) || a.title.localeCompare(b.title));
	return { collections, status, counts };
}

// Distinct collections across all runs, and how many of those you own — the
// creator summary ("14 runs · 9 collections · you own 1").
export function overlaySummary(overlays) {
	const all = new Map();
	for (const o of overlays) for (const c of o.collections) all.set(c.id, c.owned);
	return { collections: all.size, owned: [...all.values()].filter(Boolean).length };
}
